import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { Helmet } from "react-helmet";
import logo1 from "../images/QS_Logo.png";

const fadeIn = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75 } },
};

export default function ErrorPage() {
  return (
    <>
      {/* SEO / Meta */}
      <Helmet>
        <title>404 Page Not Found | Quadrangle Solutions</title>
        <meta
          name="description"
          content="Oops! The page you are looking for does not exist on Quadrangle Solutions website."
        />
        <meta name="robots" content="noindex, follow" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "404 Page Not Found",
            description:
              "Oops! The page you are looking for does not exist on Quadrangle Solutions website.",
            url: "https://www.quadranglesolutions.com/404",
          })}
        </script>
      </Helmet>

      {/* Error Page Content */}
      <section className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-green-50 flex flex-col justify-center items-center px-6">
        <motion.div
          className="max-w-md w-full text-center bg-white/95 rounded-2xl shadow-xl px-10 py-14 flex flex-col items-center border border-green-100"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          {/* Logo */}
          <motion.img
            src={logo1}
            alt="Quadrangle Solutions Logo"
            className="w-32 mb-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          />

          {/* Icon */}
          <motion.div
            className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-sky-500 text-white shadow-lg mb-6"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <AlertTriangle size={40} />
          </motion.div>

          <h1 className="text-6xl font-extrabold mb-2 bg-gradient-to-r from-green-600 to-sky-600 bg-clip-text text-transparent">
            404
          </h1>
          <h2 className="text-2xl font-bold mb-4 text-green-700">
            Page Not Found
          </h2>
          <p className="mb-8 text-gray-700 text-lg">
            Sorry, the page you’re looking for doesn’t exist or has been moved.
            If you believe this is an error, please{" "}
            <a
              href="mailto:quadsols@gmail.com"
              className="text-sky-600 underline hover:text-green-600"
            >
              contact support
            </a>
            .
          </p>

          <Link
            to="/"
            className="inline-block bg-gradient-to-r from-green-600 to-sky-600 text-white font-semibold px-8 py-3 rounded-lg shadow-lg hover:from-green-700 hover:to-sky-700 transition text-lg"
          >
            Back to Home
          </Link>
        </motion.div>
      </section>
    </>
  );
}
